/* eslint-disable react/prop-types */
import { FC } from "react";
import { Loader2 } from "lucide-react";
import type { ChunkedUploadState } from "./useChunkedVideoUpload";

const formatBytes = (bytes: number): string => {
	if (!Number.isFinite(bytes) || bytes < 0) return "0 B";
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	if (bytes < 1024 * 1024 * 1024)
		return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatEta = (seconds: number | null): string => {
	if (seconds === null || !Number.isFinite(seconds)) return "estimating…";
	if (seconds < 1) return "<1s";
	if (seconds < 60) return `${Math.round(seconds)}s`;
	const m = Math.floor(seconds / 60);
	const s = Math.round(seconds % 60);
	return `${m}m ${s}s`;
};

const statusLabel = (status: ChunkedUploadState["status"]): string => {
	if (status === "initiating") return "Preparing upload";
	if (status === "completing") return "Finalising upload";
	if (status === "uploading") return "Uploading";
	return "";
};

interface Props {
	state: ChunkedUploadState | null;
	onCancel?: () => void;
	/**
	 * Visual variant. `overlay` is a translucent panel rendered over a
	 * preview (used by the announcement editor's video preview frame);
	 * `inline` is a self-contained block suitable for a list row or a
	 * standalone upload card. Both render the same data — only the
	 * surrounding chrome differs.
	 */
	variant?: "overlay" | "inline";
}

/**
 * Shared progress UI for chunked video uploads. Driven by the state
 * returned by `useChunkedVideoUpload` (or the `videoUploadState` field
 * exposed by `useAzureFileUpload` for video routes). Use this in every
 * place the admin uploads a video so the ceremony is identical: bar,
 * percentage, bytes, MB/s, ETA, cancel link.
 *
 * Returns null when there's no active upload (status `idle`/`done`/
 * `aborted`/`error` with no in-flight bytes), so callers can keep their
 * existing layout and just drop this in unconditionally.
 */
const VideoUploadProgress: FC<Props> = ({
	state,
	onCancel,
	variant = "inline"
}) => {
	if (!state) return null;
	const active =
		state.status === "initiating" ||
		state.status === "uploading" ||
		state.status === "completing";
	if (!active) return null;

	const pct = Math.min(100, Math.max(0, state.progress * 100));
	const isOverlay = variant === "overlay";

	const wrapperClass = isOverlay
		? "absolute inset-0 flex flex-col items-center justify-center bg-black/60 text-white text-sm font-medium gap-3 p-6"
		: "flex flex-col items-stretch gap-2 rounded-lg border border-border bg-subtle p-4 text-sm";
	const barTrackClass = isOverlay
		? "w-full max-w-md h-2 rounded-full bg-white/20 overflow-hidden"
		: "w-full h-2 rounded-full bg-border/40 overflow-hidden";
	const barFillClass = isOverlay
		? "h-full bg-white transition-[width] duration-200"
		: "h-full bg-primary transition-[width] duration-200";
	const metaClass = isOverlay
		? "text-xs opacity-90 flex flex-wrap items-center justify-center gap-x-3 gap-y-1"
		: "text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1";
	const cancelClass = isOverlay
		? "text-xs underline underline-offset-2 hover:opacity-90"
		: "text-xs text-destructive underline underline-offset-2 hover:opacity-90 self-start";

	// Always render a percentage when we have any progress signal. For
	// "initiating" we show 0% — fast uploads can transition through
	// "initiating" → "uploading" → "completing" in well under a second,
	// and showing a static percentage gives the user something to anchor
	// to even when the bar barely moves.
	return (
		<div
			role="status"
			aria-live="polite"
			data-testid="video-upload-progress"
			className={wrapperClass}
		>
			<div className="flex items-center gap-2">
				<Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
				<span>
					{statusLabel(state.status)
						? `${statusLabel(state.status)} ${Math.round(pct)}%`
						: `${Math.round(pct)}%`}
				</span>
			</div>
			<div
				className={barTrackClass}
				role="progressbar"
				aria-valuemin={0}
				aria-valuemax={100}
				aria-valuenow={Math.round(pct)}
			>
				<div
					className={barFillClass}
					style={{ width: `${pct}%` }}
					data-testid="video-upload-progress-fill"
					aria-hidden="true"
				/>
			</div>
			<div className={metaClass}>
				<span>
					{formatBytes(state.bytesUploaded)} / {formatBytes(state.totalBytes)}
				</span>
				{state.speedBytesPerSecond > 0 && (
					<span>{formatBytes(state.speedBytesPerSecond)}/s</span>
				)}
				<span>ETA {formatEta(state.etaSeconds)}</span>
			</div>
			{onCancel && (
				<button type="button" onClick={onCancel} className={cancelClass}>
					Cancel upload
				</button>
			)}
		</div>
	);
};

export default VideoUploadProgress;
